// React
import { useCallback, useState } from 'react'
import { HallType } from '@weblite/ts-action/hall'
// Material
import {
  Avatar,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
// Common
import { ErrorOutlineIcon } from '@weblite/icons'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { MultilineInput, TextInput } from '$common/input'
import { CameraIcon } from '$common/icons/camera'
import { Label } from '$common/label'
import { WithEditableAvatar } from '$common/with-editable-avatar'
// Localization
import { t } from '$i18n'
// Helper
import { nothing } from '$func/utils'
import { hallConstraints } from '$logic/hall/constant'
import {
  closeCreateHall,
  openAddHallMembers,
  openDefineHallAccessibility,
} from '$logic/router'
import { getImageUrl } from '$func/imageUrl'
import { showProfilePictureMenu } from '$comp/modals/menus/profile-picture-menu'
import { EDUCATIONAL_CHANNEL_TAG, MOOKEB_TAG } from '$logic/hall/react-hook'
import { RadioButtonListItem } from '$common/radio-button-list-item'
import { createHall } from '$logic/hall'
import { useCurrentWorkSpaceId } from '$comp/main/react-hook'
import { DEFAULT_WORKSPACE_ID } from '$logic/workspace'

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  margin: 0 auto;
`
const StyledPhotoCameraIcon = styled(CameraIcon)`
  width: 50px;
  height: 50px;
`

const ChannelTypeSection = styled(FormControl)`
  padding: 10px 20px;
  background-color: white;
`

function ChannelTypeLabel({ text }: { text: string }) {
  return (
    <FormLabel>
      <Typography variant="subtitle3" color="secondary700">
        {text}
      </Typography>
    </FormLabel>
  )
}

export const enum ChannelType {
  educational = 'educational',
  mookeb = 'mookeb',
  default = 'default',
}
interface Props {
  hallType: HallType
}

export function CreateHall({ hallType }: Props) {
  const currentWorkspaceId = useCurrentWorkSpaceId()
  const workspaceId =
    currentWorkspaceId === DEFAULT_WORKSPACE_ID ? undefined : currentWorkspaceId

  // TODO: IMPLEMENT LOGIC FOR UPDATE
  const [channelType, setChannelType] = useState<ChannelType>(
    ChannelType.default,
  )

  const [imageFileId, setImageFileId] = useState<string>()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const onConfirmHandle = useCallback(async () => {
    if (!title) return
    // TODO: Show Loading

    const roomId = await createHall({
      name: title,
      type: hallType,
      description,
      image: imageFileId,
      tags:
        channelType === ChannelType.educational
          ? [EDUCATIONAL_CHANNEL_TAG]
          : channelType === ChannelType.mookeb
            ? [MOOKEB_TAG]
            : [],
      workspaceId: hallType === 'CHANNEL' ? workspaceId : undefined,
    })
    if (!roomId) return
    analite(hallType === 'CHANNEL' ? 'CREATE_CHANNEL' : 'CREATE_GROUP', {
      name: title,
      roomId,
      workspaceId,
    })

    if (hallType === 'GROUP') openAddHallMembers(roomId, true, true)
    else openDefineHallAccessibility(roomId, channelType, true)
  }, [imageFileId, title, description, hallType, channelType, workspaceId])

  const isCreatingChannel = hallType === 'CHANNEL'
  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={
            isCreatingChannel
              ? t('newChatModal.createChannel')
              : t('newChatModal.createGroup')
          }
          confirmDisabled={!title}
          onBack={closeCreateHall}
          onConfirm={onConfirmHandle}
        />
      }
    >
      <SettingsSection
        sx={{
          padding: '14px 20px 20px',
        }}
      >
        <>
          <WithEditableAvatar
            onSelectClick={() =>
              showProfilePictureMenu({
                onSubmit: setImageFileId,
                showDelete: !!imageFileId,
              })
            }
          >
            <StyledAvatar src={getImageUrl(imageFileId)} onClick={nothing}>
              <StyledPhotoCameraIcon />
            </StyledAvatar>
          </WithEditableAvatar>

          <Box sx={{ marginTop: '23px' }}>
            <Label
              title={
                isCreatingChannel
                  ? t('profile.hall.channelName')
                  : t('profile.hall.groupName')
              }
              required
            />
            <TextInput
              onChange={setTitle}
              value={title}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelName')
                  : t('profile.hall.enterGroupName')
              }
              maxLength={hallConstraints.name.maxLength}
            />

            <Label title={t('profile.hall.description')} />
            <MultilineInput
              maxLength={hallConstraints.description.maxLength}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelDescription')
                  : t('profile.hall.enterGroupDescription')
              }
              value={description}
              onChange={setDescription}
              showMaxLength
            />

            {workspaceId && isCreatingChannel && (
              <ErrorBox>
                <ErrorOutlineIcon
                  fontSize="small"
                  sx={{ color: 'neutral500' }}
                />
                <Typography color="neutral700" variant="paragraph3">
                  {t('createHall.createInWorkspace')}
                </Typography>
              </ErrorBox>
            )}
          </Box>
        </>
      </SettingsSection>

      {isCreatingChannel && (
        <ChannelTypeSection>
          <ChannelTypeLabel text={t(`hallAccessibility.type.channel`)} />
          <RadioGroup defaultValue="default" name="radio-buttons-group">
            {(
              [
                ChannelType.default,
                ChannelType.educational,
                ChannelType.mookeb,
              ] as ChannelType[]
            ).map(type => (
              <RadioButtonListItem
                onClick={() => setChannelType(type)}
                value={type}
                key={type}
                primaryText={t(`createHall.${type}.title`)}
                secondaryText={t(`createHall.${type}.description`)}
              />
            ))}
          </RadioGroup>
        </ChannelTypeSection>
      )}
    </PageWithAppBar>
  )
}

const ErrorBox = styled('div')`
  margin-top: 8px;
  display: flex;
  align-items: start;
  gap: 4px;
`
